import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { PageLayout } from "../../layouts/PageLayout";
import { CardLayout, Invoice, PageHeader } from "../../components/v3";
import { DownloadAndPrint } from "../../components/DownloadAndPrint";
import { get_date } from "../../components/generalComponets";
import { AccountService, FinanceService } from "../../services";
import { getAccountName, getDateAndTime } from "../../utils/formatters";

interface UserInfo {
  firstName: string;
  lastName: string;
}

interface TransactionReceiptData {
  date?: string;
  type?: string;
  createdBy?: UserInfo;
  createdAt?: string;
  updatedBy?: UserInfo;
  updatedAt?: string;
  toAccountName?: string;
  fromAccountName?: string;
  amount?: string | number;
  description?: string;
  fromAccountId?: string;
  toAccountId?: string;
}

export const SingleTransaction = () => {
  const [transactionData, setTransactionData] = useState<Partial<TransactionReceiptData>>({});

  const { id } = useParams();
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const fetchData = async (invoiceId) => {
    try {
      const { data: transaction } = await FinanceService.getTransactionById(invoiceId);
      let invoicePayload = { ...transaction };
      const accountIds = Array.from(new Set([transaction.fromAccountId, transaction.toAccountId].filter(Boolean)));

      if (accountIds.length > 0) {
        const { data: accounts } = await AccountService.getAccounts({
          ids: accountIds.join(","),
          limit: accountIds.length,
          includeDeleted: true,
          includeOwnerDetails: true,
          includeRestrictedTypes: true,
        });

        const fromAccount = accounts.rows.find((account) => account.id === transaction.fromAccountId);
        const toAccount = accounts.rows.find((account) => account.id === transaction.toAccountId);

        invoicePayload = {
          ...invoicePayload,
          fromAccountName: getAccountName(fromAccount?.type, fromAccount),
          toAccountName: getAccountName(toAccount?.type, toAccount),
        };
      }
      setTransactionData(invoicePayload);
    } catch (error) {
      toast.error("Failed to fetch transaction data");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  return (
    <PageLayout>
      {/* header section */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader title="Payment Receipt" />
      </Box>

      {/* table section */}
      <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
        <CardLayout>
          <div ref={printRef}>
            <Invoice
              title="Payment Receipt"
              sectionA={[
                { label: "Date", value: get_date(transactionData?.date, "DD-MM-YYYY") },
                transactionData?.type && {
                  label: "Type",
                  value: transactionData?.type,
                },
              ].filter(Boolean)}
              sectionB={[
                {
                  label: "Created By",
                  value: transactionData?.createdBy
                    ? `${transactionData?.createdBy?.firstName} ${transactionData?.createdBy?.lastName} (${getDateAndTime(
                        transactionData?.createdAt,
                      )})`
                    : "N/A",
                },
                transactionData?.updatedBy && {
                  label: "Last Updated By",
                  value: `${transactionData?.updatedBy?.firstName ?? ""} ${
                    transactionData?.updatedBy?.lastName ?? ""
                  } (${getDateAndTime(transactionData?.updatedAt)})`,
                },
              ].filter(Boolean)}
              sectionC={[
                { label: "Payment To", value: transactionData?.toAccountName },
                { label: "Received From", value: transactionData?.fromAccountName },
              ]}
              sectionD={[
                { label: "Amount", value: transactionData?.amount ? `₹ ${transactionData?.amount}` : "-" },
                { label: "Remarks", value: transactionData?.description ? transactionData.description : "N/A" },
                {
                  label: "",
                  value: "Authorized Signatory",
                },
              ]}
            />
          </div>
        </CardLayout>
      </Box>

      <DownloadAndPrint handlePrint={handlePrint} printRef={printRef} fileName={"SingleTransaction.pdf"} />
    </PageLayout>
  );
};
